import type { DamageWaiverApi } from "../../api/DamageWaiverApi";
import type { CreateDamageWaiverType } from "../../generated/dto/CreateDamageWaiverType";
import type { DamageWaiverFetchOptions } from "../../generated/dto/DamageWaiverFetchOptions";
import type { DamageWaiverMember } from "../../generated/dto/DamageWaiverMember";
import type { DamageWaiverMemberFetchOptions } from "../../generated/dto/DamageWaiverMemberFetchOptions";
import type { DamageWaiverMemberSearchResult } from "../../generated/dto/DamageWaiverMemberSearchResult";
import type { DamageWaiverSearchParams } from "../../generated/dto/DamageWaiverSearchParams";
import type { DamageWaiverStaff } from "../../generated/dto/DamageWaiverStaff";
import type { DamageWaiverStaffSearchResult } from "../../generated/dto/DamageWaiverStaffSearchResult";
import type { DamageWaiverTypeMember } from "../../generated/dto/DamageWaiverTypeMember";
import type { DamageWaiverTypeSearchParams } from "../../generated/dto/DamageWaiverTypeSearchParams";
import type { DamageWaiverTypeStaff } from "../../generated/dto/DamageWaiverTypeStaff";
import type { DamageWaiverTypeStaffSearchResult } from "../../generated/dto/DamageWaiverTypeStaffSearchResult";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { UpdateDamageWaiverType } from "../../generated/dto/UpdateDamageWaiverType";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class DamageWaiverFrontendService {
  constructor(private readonly api: DamageWaiverApi) {}
  private readonly damageWaiverTypeStaffCache: Map<number, DamageWaiverTypeStaff> = new Map();
  async getDamageWaiverMember(id: number, options?: DamageWaiverMemberFetchOptions): Promise<DamageWaiverMember> {
    return this.api.getDamageWaiverMember(id, options)
  }
  async getDamageWaiverStaff(id: number, options?: DamageWaiverFetchOptions): Promise<DamageWaiverStaff> {
    return this.api.getDamageWaiverStaff(id, options)
  }
  async getDamageWaiverTypeStaff(id: number): Promise<DamageWaiverTypeStaff> {
    const __cached = this.damageWaiverTypeStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getDamageWaiverTypeStaff(id);
    this.damageWaiverTypeStaffCache.set(id, __value);
    return __value
  }
  async getAllDamageWaiverTypeStaffs(): Promise<Array<DamageWaiverTypeStaff>> {
    return this.api.getAllDamageWaiverTypeStaffs()
  }
  async createDamageWaiverTypeStaff(source: CreateDamageWaiverType): Promise<DamageWaiverTypeStaff> {
    const __result = await this.api.createDamageWaiverTypeStaff(source);
    this.damageWaiverTypeStaffCache.clear();
    return __result
  }
  async updateDamageWaiverTypeStaff(source: UpdateDamageWaiverType): Promise<DamageWaiverTypeStaff> {
    const __result = await this.api.updateDamageWaiverTypeStaff(source);
    this.damageWaiverTypeStaffCache.clear();
    return __result
  }
  async searchDamageWaiverMember(params: DamageWaiverSearchParams, page: number, perPage: number): Promise<PaginationResult<DamageWaiverMemberSearchResult>> {
    return this.api.searchDamageWaiverMember(params, page, perPage)
  }
  async searchDamageWaiverStaff(params: DamageWaiverSearchParams, page: number, perPage: number): Promise<PaginationResult<DamageWaiverStaffSearchResult>> {
    return this.api.searchDamageWaiverStaff(params, page, perPage)
  }
  async searchDamageWaiverTypeStaff(params: DamageWaiverTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<DamageWaiverTypeStaffSearchResult>> {
    return this.api.searchDamageWaiverTypeStaff(params, page, perPage)
  }
  async searchDamageWaiverMemberFull(params: DamageWaiverSearchParams, page: number, perPage: number, options?: DamageWaiverMemberFetchOptions): Promise<PaginationResult<DamageWaiverMember>> {
    return this.api.searchDamageWaiverMemberFull(params, page, perPage, options)
  }
  async searchDamageWaiverStaffFull(params: DamageWaiverSearchParams, page: number, perPage: number, options?: DamageWaiverFetchOptions): Promise<PaginationResult<DamageWaiverStaff>> {
    return this.api.searchDamageWaiverStaffFull(params, page, perPage, options)
  }
  async searchDamageWaiverTypeStaffFull(params: DamageWaiverTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<DamageWaiverTypeStaff>> {
    return this.api.searchDamageWaiverTypeStaffFull(params, page, perPage)
  }
  async getDamageWaiverStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getDamageWaiverStaffPersonId(id, options)
  }
  async getDamageWaiverStaffDamageWaiverTypeId(id: number): Promise<DamageWaiverTypeStaff | null> {
    return this.api.getDamageWaiverStaffDamageWaiverTypeId(id)
  }
  async purchaseOptionsMember(): Promise<Array<DamageWaiverTypeMember>> {
    return this.api.purchaseOptionsMember()
  }
  async purchaseOptionsPublic(): Promise<Array<DamageWaiverTypeMember>> {
    return this.api.purchaseOptionsPublic()
  }
}
